import { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { ChevronDown } from "lucide-react";

export type CountrySelectOption = {
  value?: string;
  label: string;
  divider?: boolean;
};

type IconProps = {
  country?: string;
  label?: string;
  countryName?: string;
};

export type PhoneCountrySelectDrawerProps = {
  value?: string;
  onChange: (value: string | undefined) => void;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  options: CountrySelectOption[];
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  iconComponent?: React.ElementType<IconProps>;
  name?: string;
  "aria-label"?: string;
};

function getSelectedOption(
  options: CountrySelectOption[],
  country?: string,
): CountrySelectOption | undefined {
  for (const opt of options) {
    if (opt.divider) continue;
    const v = opt.value;
    if (v === country) return opt;
    if (
      (v === undefined || v === null) &&
      (country === undefined || country === null)
    ) {
      return opt;
    }
  }
  return undefined;
}

function optionKey(opt: CountrySelectOption, index: number): string {
  if (opt.divider) return `divider-${index}`;
  return opt.value ?? "ZZ";
}

function optionMatchesCountry(
  opt: CountrySelectOption,
  country: string | undefined,
): boolean {
  if (opt.divider) return false;
  const o = opt.value;
  if (o === undefined || o === null || o === "ZZ") {
    return country === undefined || country === null;
  }
  return o === country;
}

/** Strip dial-code prefix so "India" matches even if label is "+91 India". */
function labelForSearch(label: string): string {
  const trimmed = label.trim();
  const m = trimmed.match(/[A-Za-zÀ-ÿ].*/);
  return (m ? m[0] : trimmed).toLowerCase();
}

/**
 * Custom country selector for react-phone-number-input: same panel styling as Alert category dropdown.
 * Keeps PhoneInputCountry layout; only replaces the native &lt;select&gt; list UI.
 */
export default function PhoneCountrySelectDrawer({
  value,
  onChange,
  onFocus,
  onBlur,
  options,
  disabled,
  readOnly,
  className,
  iconComponent: Icon,
  name,
  "aria-label": ariaLabel,
}: PhoneCountrySelectDrawerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const typeAheadBufferRef = useRef("");
  const typeAheadClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastSingleCharIndexRef = useRef(-1);

  const selectedOption = useMemo(
    () => getSelectedOption(options, value),
    [options, value],
  );

  const selectableOptions = useMemo(
    () => options.filter((o) => !o.divider),
    [options],
  );

  const isDisabled = Boolean(disabled || readOnly);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  useEffect(() => {
    if (!open) {
      typeAheadBufferRef.current = "";
      lastSingleCharIndexRef.current = -1;
      if (typeAheadClearTimerRef.current) {
        clearTimeout(typeAheadClearTimerRef.current);
        typeAheadClearTimerRef.current = null;
      }
      return;
    }
    const id = requestAnimationFrame(() => {
      listRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const emitChange = (opt: CountrySelectOption) => {
    const raw = opt.value;
    if (raw === undefined || raw === null || raw === "ZZ") {
      onChange(undefined);
    } else {
      onChange(raw);
    }
    setOpen(false);
  };

  const scrollOptionIntoView = (opt: CountrySelectOption, index: number) => {
    const key = optionKey(opt, index);
    const el = itemRefs.current.get(key);
    el?.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  const findMatchIndex = (prefix: string, startAfter: number) => {
    const p = prefix.toLowerCase();
    if (!p) return -1;
    for (let i = 0; i < selectableOptions.length; i++) {
      const idx = (startAfter + 1 + i) % selectableOptions.length;
      const o = selectableOptions[idx];
      const name = labelForSearch(o.label);
      if (name.startsWith(p)) return idx;
    }
    return -1;
  };

  const handleListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      requestAnimationFrame(() => triggerRef.current?.focus());
      return;
    }

    if (e.key.length === 1 && /[a-zA-ZÀ-ÿ]/.test(e.key)) {
      const ch = e.key.toLowerCase();
      e.preventDefault();
      e.stopPropagation();

      if (typeAheadClearTimerRef.current) {
        clearTimeout(typeAheadClearTimerRef.current);
        typeAheadClearTimerRef.current = null;
      }

      const buf = typeAheadBufferRef.current;
      let prefix: string;
      let startAfter = -1;

      if (buf.length === 1 && buf === ch) {
        prefix = ch;
        startAfter = lastSingleCharIndexRef.current;
      } else if (buf.length > 0 && buf !== ch) {
        prefix = `${buf}${ch}`.slice(0, 12);
        lastSingleCharIndexRef.current = -1;
      } else if (buf.length === 0) {
        prefix = ch;
        startAfter = -1;
        lastSingleCharIndexRef.current = -1;
      } else {
        prefix = `${buf}${ch}`.slice(0, 12);
        lastSingleCharIndexRef.current = -1;
      }

      typeAheadBufferRef.current = prefix;

      const idx = findMatchIndex(prefix, startAfter);
      if (idx >= 0) {
        const opt = selectableOptions[idx];
        const realIndex = options.indexOf(opt);
        if (prefix.length === 1) {
          lastSingleCharIndexRef.current = idx;
        }
        scrollOptionIntoView(opt, realIndex >= 0 ? realIndex : idx);
      }

      typeAheadClearTimerRef.current = setTimeout(() => {
        typeAheadBufferRef.current = "";
        lastSingleCharIndexRef.current = -1;
        typeAheadClearTimerRef.current = null;
      }, 600);
    }
  };

  return (
    <div ref={rootRef} className="PhoneInputCountry">
      <button
        ref={triggerRef}
        type="button"
        name={name}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={isDisabled}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={() => {
          if (!isDisabled) setOpen((o) => !o);
        }}
        className={classNames(
          "relative z-[2] flex h-full min-h-[2.75rem] shrink-0 items-center gap-1 rounded-l-lg border-0 bg-transparent px-2 outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:text-white/90",
          className,
        )}
      >
        {value && Icon ? (
          <Icon
            country={value}
            label={selectedOption?.label ?? ""}
          />
        ) : null}
        <ChevronDown
          className={classNames(
            "h-4 w-4 shrink-0 text-gray-500 transition-transform dark:text-gray-400",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>

      {open && !isDisabled && (
        <div
          className="absolute left-0 top-full z-[100] mt-1 min-w-full overflow-hidden rounded-xl border border-gray-300 bg-gray-200 shadow-lg dark:border-gray-700 dark:bg-gray-900"
          role="listbox"
        >
          <div
            ref={listRef}
            tabIndex={-1}
            onKeyDown={handleListKeyDown}
            className="max-h-[200px] overflow-y-auto py-1 scrollbar-hide no-scrollbar outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 focus-visible:ring-inset dark:bg-[#111f33]"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {options.map((opt, index) => {
              if (opt.divider) return null;
              const selected = optionMatchesCountry(opt, value);
              const refKey = optionKey(opt, index);

              return (
                <button
                  key={refKey}
                  ref={(el) => {
                    if (el) itemRefs.current.set(refKey, el);
                    else itemRefs.current.delete(refKey);
                  }}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={classNames(
                    "alert-cooldown-option flex w-full cursor-pointer flex-col gap-0.5 px-2.5 py-2.5 text-left text-sm text-gray-800 transition-colors dark:text-white/90 dark:bg-[#111f33]",
                    selected &&
                      "text-black dark:!text-white dark:bg-[#162640] bg-gray-300",
                  )}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => emitChange(opt)}
                >
                  <span className="font-medium">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
